import { useEffect, useState } from "react";
import { Megaphone, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";

const KIND_LABELS = {
    feature: { ar: "ميزة جديدة", en: "New Feature", color: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
    update: { ar: "تحديث", en: "Update", color: "bg-blue-50 text-blue-800 ring-blue-200" },
    announcement: { ar: "إعلان", en: "Announcement", color: "bg-amber-50 text-amber-800 ring-amber-200" },
};

export default function WhatsNew() {
    const navigate = useNavigate();
    const { lang } = useLang();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [marking, setMarking] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/announcements");
            setItems(r.data.items || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // Mark all as read on open — WhatsApp-style: visiting the screen
        // clears the badge.
        api.post("/announcements/read-all").catch(() => {});
    }, []);

    const markAll = async () => {
        setMarking(true);
        try {
            await api.post("/announcements/read-all");
            await load();
            toast.success(lang === "ar" ? "تم تعليم الكل كمقروء" : "All marked as read");
        } finally { setMarking(false); }
    };

    return (
        <AppShell>
            <div className="mx-auto max-w-3xl" dir={lang === "ar" ? "rtl" : "ltr"}>
                <div className="mb-5 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => navigate("/")} className="gap-1 shrink-0" data-testid="whats-new-back-home-btn">
                            <ArrowRight className="h-4 w-4" />
                            {lang === "ar" ? "الرئيسية" : "Home"}
                        </Button>
                        <div>
                            <h1 className="font-heading text-2xl font-bold text-neutral-900">
                                {lang === "ar" ? "ما الجديد" : "What's New"}
                            </h1>
                            <p className="mt-1 text-sm text-neutral-500">
                                {lang === "ar" ? "آخر التحديثات والإعلانات على نظام معيار" : "Latest Meyar updates and announcements"}
                            </p>
                        </div>
                    </div>
                    <Button variant="outline" onClick={markAll} disabled={marking} className="gap-2" data-testid="whats-new-mark-all">
                        {marking ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        {lang === "ar" ? "تعليم الكل كمقروء" : "Mark all read"}
                    </Button>
                </div>

                {loading ? (
                    <div className="py-14 text-center text-sm text-neutral-500">...جاري التحميل</div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl bg-white p-10 text-center ring-1 ring-neutral-200">
                        <Megaphone className="mx-auto h-10 w-10 text-neutral-300" />
                        <p className="mt-4 text-sm text-neutral-500">
                            {lang === "ar" ? "لا توجد تحديثات بعد" : "No announcements yet"}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {items.map((a) => {
                            const kind = KIND_LABELS[a.kind] || KIND_LABELS.update;
                            const title = lang === "ar" ? a.title_ar : (a.title_en || a.title_ar);
                            const body = lang === "ar" ? a.body_ar : (a.body_en || a.body_ar);
                            return (
                                <article key={a.id} className={`rounded-2xl bg-white p-5 ring-1 ${a.is_read ? "ring-neutral-200" : "ring-amber-300"} shadow-sm`} data-testid={`whats-new-item-${a.id}`}>
                                    <div className="flex items-center gap-2">
                                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ring-1 ${kind.color}`}>
                                            {lang === "ar" ? kind.ar : kind.en}
                                        </span>
                                        {!a.is_read ? <span className="h-2 w-2 rounded-full bg-red-500" /> : null}
                                        <span className="ms-auto text-xs text-neutral-400">
                                            {a.created_at ? new Date(a.created_at).toLocaleDateString("ar-SA") : ""}
                                        </span>
                                    </div>
                                    <h2 className="mt-2 font-heading text-lg font-bold text-neutral-900">{title}</h2>
                                    <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-neutral-700">{body}</p>
                                </article>
                            );
                        })}
                    </div>
                )}
            </div>
        </AppShell>
    );
}
